import { EntityStringId } from 'app/types/entity';
import { useCallback } from 'react';
import { createStorageHook } from 'app/components/storage/createStorageHook';
import { StorageType } from 'app/utils/StorageContext';
import { getOrElse } from 'fp-ts/lib/Either';
import { decodeOrElse, MapCodec } from 'app/utils/ioTs';
import { pipe } from 'fp-ts/lib/pipeable';
import { is, Map } from 'immutable';
import * as t from 'io-ts';
import { LAST_SYNCED_NAVIGATION_TIMESTAMP_MAP, LAST_SYNCED_LOCATION_KEY_MAP } from 'app/models/sessionStorage/constants';
import { IdentityEntityStringIdCodec } from 'app/types/entity/codecs';

const timestampMapParser = {
  serialize: (value: Map<EntityStringId, number>) => value.toJS(),
  deserialize: (stored: unknown): Map<EntityStringId, number> => {
    return Map(
      pipe(
        t.dictionary(IdentityEntityStringIdCodec, t.number).decode(stored),
        getOrElse(() => ({})),
      ),
    );
  },
  isEqual: is,
};

const useLastSyncedNavigationTimestampMap = createStorageHook(
  LAST_SYNCED_NAVIGATION_TIMESTAMP_MAP,
  timestampMapParser,
  StorageType.SESSION,
);

export const useLastSyncedTimestamp = (rtcRoom: EntityStringId | null) => {
  const [map, setMap] = useLastSyncedNavigationTimestampMap();

  const setLastSyncedTimestamp = useCallback((timestamp: number) => {
    if (rtcRoom) {
      const newValue = (map ?? Map()).set(rtcRoom, timestamp);

      if (!is(newValue, map)) {
        setMap(newValue);
      }
    }
  }, [rtcRoom, map, setMap]);

  const lastSyncedTimestamp = rtcRoom && map?.get(rtcRoom) || null;

  return [lastSyncedTimestamp, setLastSyncedTimestamp] as const;
};

type LastSyncedKeyMap = Map<EntityStringId, string>;

const LastSyncedKeyMapCodec: t.Type<LastSyncedKeyMap, Array<[EntityStringId, string]>, unknown> =
  t.array(t.tuple([IdentityEntityStringIdCodec, t.string]))
  .pipe(MapCodec(IdentityEntityStringIdCodec, t.string));

const useLastSyncedLocationKeyStorage = createStorageHook(
  LAST_SYNCED_LOCATION_KEY_MAP,
  {
    serialize: (value: LastSyncedKeyMap) => LastSyncedKeyMapCodec.encode(value),
    deserialize: (value: unknown) => decodeOrElse(LastSyncedKeyMapCodec, value, () => Map()),
  },
  StorageType.SESSION,
);

export const useLastSyncedLocationKey = (rtcRoom: EntityStringId | null) => {
  const [map, setMap] = useLastSyncedLocationKeyStorage();

  const setLastSyncedLocationKey = useCallback((key: string) => {
    if (!rtcRoom) {
      return;
    }

    setMap((map || Map()).set(rtcRoom, key));
  }, [rtcRoom, map, setMap]);

  return [
    rtcRoom && map && map.get(rtcRoom) || null,
    setLastSyncedLocationKey,
  ] as const;
};
